import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Location } from '@angular/common';
import { AuthValidator } from '../libraries/auth-validator/auth-validator';
import { ProfileService } from '../services/profile/profile.service';
import { Constants } from '../constants';
import { SharedDataService } from '../services/shared-data/shared-data.service';
import { filter, Observable, switchMap } from 'rxjs';
import { RaInjectOneidAuthTokenService } from '@wdpr/ra-angular-oneid-auth-token-injector';
import { ConfigService } from '../services/config/config.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuardService implements CanActivate {

  constructor(
    private _profileService: ProfileService,
    private _authValidator: AuthValidator,
    private _sharedDataService: SharedDataService,
    private _raInjectOneidAuthTokenService: RaInjectOneidAuthTokenService,
    private _configService: ConfigService,
    private _location: Location
  ) {}

  /**
   * Guard page with valid profile and swid
   *
   * @returns {any}
   */
  canActivate(): any {
    this._setReferrer();

    const enableProfileAuthenticator = (<any>this._configService.getConfig())?.enableProfileAuthenticator;

    return enableProfileAuthenticator ? this._getProfileWithProfileAuthenticator() : this._getProfileWithAuthCookie();
  }

  /**
   * @desc Get Profile with profile authenticator
   * @returns
   */
  private _getProfileWithProfileAuthenticator() {

    return this._sharedDataService.getValue('profileAuthInitialized')
      .pipe(
        filter((value) => {
          return value !== undefined;
        }),
        switchMap((profileAuth: any) => {

          if (profileAuth) {

            return this._authTokenInit().then(() => {

              return this._authValidator.handleGuestLogin().then(result => {
                if (result) {
                  // if login is successful but getting profile fails, then redirect to login page for page reset
                  return this._profileService
                    .load(result)
                    .then((profile) => {
                      return profile && profile.swid ? true : this._redirectToLogin();
                    })
                    .catch((err) => {
                      return this._redirectToLogin();
                    });
                // if login modal was closed, then go to home page (current login page behavior)
                } else {
                  return this._redirectToHomepage();
                }
              });
            });
          // profile auth doesn't exist, redirect to login
          } else {
            return this._redirectToLogin();
          }
        })
      );
  }

  /**
   * @desc get profile with auth cookie
   *   - legacy flow
   */
  private _getProfileWithAuthCookie() {
    return this._profileService
      .load()
      .then((profile) => {
        return this._authValidator.verifyAuthCookies() && profile && profile.swid ?
          true : this._redirectToLogin();
      })
      .catch((err) => {
        return this._redirectToLogin();
      });
  }

  /**
   * @desc Initialize RA Lib for Auth Token handle
   * @returns {Promise<void>}
   */
  private _authTokenInit() {
    return this._raInjectOneidAuthTokenService.init({
      ENABLED: true,
      NONLOGGEDINFLOW: false,
      LOGGEDINFLOW: true
    });
  }

  /**
   * Set referrer into Local Storage before redirect to the next route.
   */
  private _setReferrer() {
    const currentRoute = this._location.path().replace('/', '');

    if (
      !!document.referrer &&
      document.referrer.indexOf('login') === -1 &&
      document.referrer.indexOf('queue') === -1 &&
      !currentRoute
    ) {
      localStorage.setItem('claimReferrer', document.referrer);
    }
  }

  /**
   * Redirect to login screen
   *
   * @returns {Promise<unknown>}
   */
  private _redirectToLogin(): Promise<unknown> {
    window.location.href = window.location.origin + Constants.REDIRECT_LOGIN_PATH;

    return new Promise((resolve, reject) => { });
  }

  /**
   * Redirect to home screen
   *
   * @returns {Promise<unknown>}
   */
   private _redirectToHomepage(): Promise<unknown> {
    window.location.href = window.location.origin;

    return new Promise((resolve, reject) => { });
  }
}
